import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import { shouldReportBoardError } from "../boardFailures";

import { boardQueryKeys } from "./boardQueryKeys";

/**
 * Discard a board draft: delete it from this browser and invalidate the org's
 * board lists. The draft's own record stays cached, as it does when the draft
 * is saved, so the page showing the draft keeps it on screen until that page
 * goes to the boards list rather than reading the draft again and finding it
 * gone.
 */
export function useDiscardBoardDraft() {
  const { boardService } = useDomainServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ boardId, orgId }: { boardId: string; orgId: string }) =>
      boardService.deleteBoard(boardId, orgId),
    onSuccess: (_, { orgId }) =>
      queryClient.invalidateQueries({ queryKey: boardQueryKeys.list(orgId) }),
    onError: (error) => {
      if (shouldReportBoardError(error)) {
        ErrorMonitoringService.captureError(error);
      }
    },
  });
}
